import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraHeading, SpecraMetricStrip, SpecraStatusDot, SpecraSurface, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link } from 'react-router-dom';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { DRILL_STATE, drillHref, toneOf, valueText } from './readings';
const TONE_TEXT = {
    success: 'text-success',
    danger: 'text-danger',
    neutral: 'text-muted-foreground'
};
export function KpiPanel({ kpis, filter, programIds, loading }) {
    const byId = new Map(kpis.map((k)=>[
            k.id,
            k
        ]));
    const onTime = byId.get('on_time');
    const tiles = [
        'late_now',
        'due_next_14_days',
        'waiting_on_approval'
    ].flatMap((id)=>{
        const k = byId.get(id);
        if (!k) return [];
        return [
            {
                id,
                label: k.label,
                value: valueText(k),
                muted: !k.hasValue,
                tone: toneOf(k),
                info: k.rule,
                // The count that puts it off target first, then the target.
                note: [
                    k.detail,
                    `Target: ${k.target}`
                ].filter(Boolean).join(' · '),
                href: drillHref(DRILL_STATE[id], filter, programIds)
            }
        ];
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]",
        children: [
            /*#__PURE__*/ _jsx(Verdict, {
                k: onTime,
                filter: filter,
                programIds: programIds,
                loading: loading
            }),
            /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                getItemProps: (m)=>({
                        'data-kpi-tone': byId.get(m.id)?.tone ?? ''
                    }),
                getItemTestId: ()=>Testids.commandCenter.kpiTile,
                loading: loading && tiles.length === 0,
                metrics: tiles,
                renderLink: renderRouterLink
            })
        ]
    });
}
function Verdict({ k, filter, programIds, loading }) {
    const tone = toneOf(k);
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        "data-kpi-tone": k?.tone ?? '',
        "data-testid": Testids.commandCenter.verdict,
        padding: "md",
        radius: "lg",
        tone: "card",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraStatusDot, {
                        tone: tone
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "font-medium uppercase tracking-[var(--ls-ui-eyebrow)]",
                        size: "micro",
                        tone: "muted",
                        children: k?.label ?? 'On-time delivery'
                    }),
                    k?.rule ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                        className: "max-w-72",
                        content: k.rule,
                        delayDuration: 150,
                        children: /*#__PURE__*/ _jsx("span", {
                            "aria-label": "About on-time delivery",
                            className: "inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-muted-foreground/50 text-[length:var(--fs-ui-micro)] font-bold leading-none text-muted-foreground",
                            "data-metric-info": "",
                            role: "img",
                            children: "i"
                        })
                    }) : null
                ]
            }),
            loading && !k ? /*#__PURE__*/ _jsx("div", {
                "aria-hidden": "true",
                className: "mt-[var(--space-3)] h-10 w-24 animate-pulse rounded-[var(--radius-sm)] bg-muted"
            }) : /*#__PURE__*/ _jsx(Link, {
                className: "mt-[var(--space-2)] block w-fit rounded-[var(--radius-sm)] hover:underline",
                to: drillHref(DRILL_STATE.on_time, filter, programIds),
                children: /*#__PURE__*/ _jsx(SpecraHeading, {
                    className: TONE_TEXT[tone] ?? TONE_TEXT.neutral,
                    size: k?.hasValue ? 'xl' : 'md',
                    children: valueText(k)
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                className: "mt-[var(--space-1)]",
                size: "caption",
                tone: "secondary",
                children: [
                    "Target: ",
                    k?.target || '100%'
                ]
            }),
            k?.hasValue ? /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                className: "mt-[var(--space-2)]",
                size: "caption",
                tone: "muted",
                children: [
                    k.numerator.toLocaleString(),
                    " of ",
                    k.denominator.toLocaleString(),
                    " deliverables that came due were approved on time. An overdue deliverable with nothing filed counts as a miss."
                ]
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "mt-[var(--space-2)]",
                size: "caption",
                tone: "muted",
                children: "Nothing has come due in this view yet, so there is no on-time share to show. It starts counting at the first approval or the first overdue deliverable."
            })
        ]
    });
}
